import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  useBankAccounts,
  useBankTransactions,
  useAutoReconcile,
  useBankConnections,
  useAuthorizeBankConnection,
  useSyncBankConnection,
  useDeleteBankConnection,
} from '@/hooks/useApi';
import PageSkeleton from '@/components/PageSkeleton';
import Pagination from '@/components/Pagination';
import { BuildingLibraryIcon, ArrowPathIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';

export default function BankPage() {
  const { t } = useTranslation();
  const { data: accounts, isLoading: loadingAccounts } = useBankAccounts();
  const { data: connections, isLoading: loadingConnections } = useBankConnections();
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [page, setPage] = useState(1);
  const { data: transactions, isLoading: loadingTx } = useBankTransactions(selectedAccount, { page });
  const reconcile = useAutoReconcile();
  const authorize = useAuthorizeBankConnection();
  const syncConn = useSyncBankConnection();
  const deleteConn = useDeleteBankConnection();

  function handleSelectAccount(id: string) {
    setSelectedAccount(id);
    setPage(1);
  }

  function handleReconcile() {
    if (!selectedAccount) return;
    reconcile.mutate(undefined);
  }

  async function handleConnect() {
    const result = await authorize.mutateAsync();
    if (result?.redirectUrl) {
      window.location.href = result.redirectUrl;
    }
  }

  async function handleSync(connectionId: string) {
    const result = await syncConn.mutateAsync(connectionId);
    toast.success(
      t('bank.syncResult', {
        imported: result.transactionsImported,
        reconciled: result.reconciled,
      }),
    );
  }

  async function handleDisconnect(connectionId: string) {
    if (!window.confirm(t('bank.disconnectConfirm'))) return;
    await deleteConn.mutateAsync(connectionId);
    toast.success(t('bank.disconnected'));
  }

  if (loadingAccounts || loadingConnections) return <PageSkeleton variant="grid" />;

  return (
    <div>
      <div className="page-header">
        <h1 className="text-2xl font-bold">{t('bank.title')}</h1>
        <div className="flex gap-2">
          <button className="btn btn-secondary" onClick={handleConnect} disabled={authorize.isPending}>
            <PlusIcon className="w-4 h-4" />
            {authorize.isPending ? t('bank.connecting') : t('bank.connectBank')}
          </button>
          <button className="btn btn-primary" onClick={handleReconcile} disabled={!selectedAccount || reconcile.isPending}>
            {t('bank.reconcile')}
          </button>
        </div>
      </div>

      {/* Connections */}
      {connections && connections.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">{t('bank.connections')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {connections.map((c) => (
              <div key={c.id} className="card card-body flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                  {c.bankLogoUrl ? (
                    <img src={c.bankLogoUrl} alt={c.bankName ?? ''} className="w-8 h-8 rounded" />
                  ) : (
                    <BuildingLibraryIcon className="w-5 h-5 text-primary-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium truncate">{c.bankName || t('bank.bankConnection')}</p>
                    <span
                      className={
                        'badge ' +
                        (c.status === 'active'
                          ? 'badge-green'
                          : c.status === 'pending'
                          ? 'badge-gray'
                          : 'badge-red')
                      }
                    >
                      {t(`bank.status.${c.status}`)}
                    </span>
                  </div>
                  {c.lastSyncAt && (
                    <p className="text-xs text-gray-500 mt-1">
                      {t('bank.lastSyncAt')}: {new Date(c.lastSyncAt).toLocaleString()}
                    </p>
                  )}
                  {c.lastError && (
                    <p className="text-xs text-red-500 mt-1 truncate" title={c.lastError}>
                      {c.lastError}
                    </p>
                  )}
                  <div className="flex gap-2 mt-3">
                    <button
                      className="btn btn-xs btn-secondary"
                      onClick={() => handleSync(c.id)}
                      disabled={syncConn.isPending || c.status !== 'active'}
                    >
                      <ArrowPathIcon className="w-3.5 h-3.5" />
                      {t('bank.syncNow')}
                    </button>
                    <button
                      className="btn btn-xs btn-danger"
                      onClick={() => handleDisconnect(c.id)}
                      disabled={deleteConn.isPending}
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                      {t('bank.disconnect')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Accounts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {loadingAccounts && Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="card card-body"><div className="skeleton h-4 w-32 mb-2" /><div className="skeleton h-6 w-24" /></div>
        ))}
        {accounts?.map((acc) => (
          <button
            key={acc.id}
            onClick={() => handleSelectAccount(acc.id)}
            className={`card card-body text-left transition ${selectedAccount === acc.id ? 'ring-2 ring-primary-500' : 'hover:border-gray-300'}`}
          >
            <p className="text-sm text-gray-500">{acc.name || t('bank.account')}</p>
            <p className="font-mono font-medium">{acc.iban}</p>
            {acc.consentValid ? (
              <p className="text-xs text-green-500 mt-1">{t('bank.consentActive')}</p>
            ) : (
              <p className="text-xs text-red-500 mt-1">{t('bank.consentExpired')}</p>
            )}
          </button>
        ))}
      </div>

      {/* Transactions */}
      {selectedAccount && (
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold">{t('bank.transactions')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('bank.date')}</th>
                  <th>{t('bank.description')}</th>
                  <th>{t('bank.counterpart')}</th>
                  <th className="text-right">{t('common.amount')}</th>
                  <th>{t('bank.reconciled')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loadingTx && Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>{Array.from({ length: 5 }).map((_, j) => <td key={j}><div className="skeleton h-4 w-20" /></td>)}</tr>
                ))}
                {transactions?.data?.map((tx) => (
                  <tr key={tx.id} className={`${tx.type === 'credit' ? 'row-green' : 'row-red'} ${tx.reconciled ? 'opacity-60' : ''}`}>
                    <td className="text-gray-500">{new Date(tx.valueDate).toLocaleDateString()}</td>
                    <td className="text-gray-900">{tx.freeCommunication || tx.structuredCommunication || '—'}</td>
                    <td className="text-gray-600 text-sm">
                      {tx.counterpartName || '—'}
                      {tx.counterpartAccount && <span className="block text-xs text-gray-400 font-mono">{tx.counterpartAccount}</span>}
                    </td>
                    <td className={`text-right font-medium ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.type === 'credit' ? '+' : '-'}€{Math.abs(Number(tx.amount)).toLocaleString('de-BE', { minimumFractionDigits: 2 })}
                    </td>
                    <td>
                      {tx.reconciled ? (
                        <span className="badge badge-green">{t('bank.matched')}</span>
                      ) : (
                        <span className="badge badge-gray">{t('bank.unmatched')}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {!loadingTx && (!transactions?.data || transactions.data.length === 0) && (
                  <tr><td colSpan={5} className="text-center text-gray-400 py-12">{t('common.noResults')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination meta={transactions?.meta} onPageChange={setPage} />
        </div>
      )}

      {!selectedAccount && !loadingAccounts && (
        <div className="text-center py-12 text-gray-400">
          {accounts && accounts.length > 0
            ? t('bank.selectAccount')
            : connections && connections.length > 0
            ? t('bank.noAccountsButConnections')
            : t('bank.noAccounts')}
        </div>
      )}
    </div>
  );
}
